import React, { useCallback, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { RoutePaths } from "constants/RoutePaths";
import { useAuthContext } from "hooks/useAuthContext";
import { useGetTags } from "hooks/useGetTags";
import { useGetTrainingWithVersionsAndProgress } from "hooks/useGetTrainingWithVersionsAndProgress";
import { useGetLessonImage } from "hooks/useGetLessonImage";
import { NodeEditor } from "components/views/editor/components/NodeEditor";
import { useTrainingPageData } from "hooks/useTrainingPageData";
import { useTrainingPageCallbacks } from "hooks/useTrainingPageCallbacks";
import { useEditorUnsavedChangesGuard } from "hooks/useEditorUnsavedChangesGuard";
import { useExportHandlers } from "hooks/useExportHandlers";
import { useImportHandler } from "hooks/useImportHandler";
import { Image } from "components/views/editor/models/Image";
import { findAnyVersionByLanguage, getInitialVersion } from "components/views/editor/helpers/versionUtils";
import { buildLessonForm } from "components/views/editor/helpers/lessonFormHelper";
import { useUpdateVrLesson } from "hooks/useUpdateVrLesson";
import { UpdateLessonPageLayout } from "components/views/editor/UpdateLessonPageLayout";
import { LoadingBox } from "components/views/editor/UpdateTrainingPage.styles";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { LessonsTags } from "models/LessonsTags";
import { LessonType } from "models/LessonType";
import { LessonVersion, VrLessonVersion } from "models/LessonVersion";

const StyledHiddenInput = styled("input")({
    display: "none"
});

interface UpdateVrLessonPageProps {
    lessonData: IGetLessonWithVersionsResponse;
    fetchLessonWithLatestVersions: () => Promise<unknown>;
}

export const UpdateVrLessonPage = ({ lessonData, fetchLessonWithLatestVersions }: UpdateVrLessonPageProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { onWorkspaceChanged } = useAuthContext();

    const [currentLanguage, setCurrentLanguage] = useState<string>("");
    const [currentVersion, setCurrentVersion] = useState<LessonVersion | null>(null);
    const [lessonImage, setLessonImage] = useState<Image | null>(null);
    const [pendingNewVersion, setPendingNewVersion] = useState<string | null>(null);

    const {
        lessonFormData,
        setLessonFormData,
        editor,
        setEditor,
        setGameEditorData,
        tags,
        tagsLoading,
        isLessonFormValid,
        isNodeEditorValid,
        resetFormErrors,
        formValidationErrors
    } = useTrainingPageData();

    const { fetchTags } = useGetTags();
    const { fetchLessonImage, loading: fetchImagePending } = useGetLessonImage();
    const { fetchTrainingWithVersionsAndProgress, fetchTrainingWithVersionsAndProgressPending } = useGetTrainingWithVersionsAndProgress();
    const { updateVrLesson, isUpdating } = useUpdateVrLesson();

    const { handleFormDataChange, onEditorChange } = useTrainingPageCallbacks({
        setLessonFormData,
        setEditor,
        setGameEditorData: setGameEditorData,
        formValidationErrors,
        resetFormErrors
    });

    const {
        isDialogOpen,
        guardedAction,
        handleCancelDialog,
        handleConfirmDialog,
        setBlockerActive
    } = useEditorUnsavedChangesGuard({
        editor,
        savedConfig: currentVersion && "nodesConfiguration" in currentVersion ? currentVersion.nodesConfiguration : undefined
    });

    const { handleExportClick, handleExportToVrClick } = useExportHandlers({
        lessonData,
        lessonFormData,
        editor,
        isLessonFormValid,
        isNodeEditorValid,
        fetchTrainingWithVersionsAndProgress,
        fetchTrainingWithVersionsAndProgressPending
    });

    const { inputRef, handleImportClick, handleImportFileOnChange } = useImportHandler({ editor, enqueueSnackbar });

    const isCurrentVersionNew = pendingNewVersion === currentLanguage;

    const switchLanguage = useCallback(
        (language: string) => {
            if (!lessonData) return;

            const version = findAnyVersionByLanguage(lessonData.versions, language);

            if (!version) {
                setCurrentLanguage(language);
                const pendingVersion: VrLessonVersion = {
                    type: LessonType.VR,
                    id: "",
                    lessonId: lessonData.lessonId,
                    lessonName: lessonData.lessonName,
                    maxPoints: 0,
                    version: 0,
                    createdAt: "",
                    jsonConfig: {},
                    language,
                    name: "",
                    description: null,
                    nodesConfiguration: {},
                    isDefault: false
                };
                setCurrentVersion(pendingVersion);
                setLessonFormData(buildLessonForm(lessonData, pendingVersion, lessonImage));
                return;
            }

            setCurrentLanguage(language);
            setCurrentVersion(version);
            setLessonFormData(buildLessonForm(lessonData, version, lessonImage));
        },
        [lessonData, lessonImage, setLessonFormData]
    );

    const handleLanguageChange = useCallback(
        (language: string) => {
            guardedAction(() => switchLanguage(language));
        },
        [guardedAction, switchLanguage]
    );

    const handleRefresh = useCallback(() => {
        guardedAction(fetchLessonWithLatestVersions);
    }, [guardedAction, fetchLessonWithLatestVersions]);

    const handleAddNewVersion = useCallback(
        (language: string) => {
            if (pendingNewVersion !== null) {
                guardedAction(() => {
                    setPendingNewVersion(language);
                    switchLanguage(language);
                });
            } else {
                setPendingNewVersion(language);
                guardedAction(() => switchLanguage(language));
            }
        },
        [pendingNewVersion, guardedAction, switchLanguage]
    );

    const handleSaveClick = async () => {
        if (!lessonData || !lessonFormData || !currentVersion) return;
        if (!isLessonFormValid(lessonFormData)) return;
        if (editor && editor.nodes.length > 0 && !isNodeEditorValid(editor)) return;

        const success = await updateVrLesson(
            lessonData.lessonId,
            lessonFormData,
            currentLanguage,
            editor
        );

        if (!success) return;

        if (isCurrentVersionNew) {
            setPendingNewVersion(null);
        }

        await fetchTags();
        await fetchLessonWithLatestVersions();

        enqueueSnackbar(t("lessonEditor.saveSuccess"), { variant: "success" });
    };

    useEffect(() => {
        if (!lessonData) return;

        const userLanguage = i18n.language || "en";
        const versionToLoad =
            getInitialVersion(lessonData.versions, userLanguage) ??
            (lessonData.versions.length === 0
                ? ({
                      type: LessonType.VR,
                      id: "",
                      lessonId: lessonData.lessonId,
                      lessonName: lessonData.lessonName,
                      maxPoints: 0,
                      version: 0,
                      createdAt: "",
                      jsonConfig: {},
                      language: userLanguage,
                      name: "",
                      description: null,
                      nodesConfiguration: {},
                      isDefault: false
                  } as VrLessonVersion)
                : null);

        if (!versionToLoad) return;

        setCurrentLanguage(versionToLoad.language);
        setCurrentVersion(versionToLoad);
        setLessonFormData(buildLessonForm(lessonData, versionToLoad, lessonImage));
        setGameEditorData({ fileId: null, launchParams: "", events: [] });
    }, [lessonData, lessonImage, setLessonFormData, setGameEditorData]);

    useEffect(() => {
        const loadLessonImage = async () => {
            if (!lessonData?.photoFileId) {
                setLessonImage(null);
                return;
            }

            const image = await fetchLessonImage(lessonData.photoFileId);
            setLessonImage(image);
        };

        loadLessonImage();
    }, [lessonData?.photoFileId, fetchLessonImage]);

    useEffect(() => {
        const handleWorkspaceChange = () => {
            setBlockerActive(false);
            navigate(RoutePaths.EDITOR);
        };

        const unsubscribe = onWorkspaceChanged(handleWorkspaceChange);
        return () => unsubscribe();
    }, [navigate, onWorkspaceChanged, setBlockerActive]);

    const tagsOrEmpty: LessonsTags = tags ?? { tagsA: [], tagsB: [] };

    if (!lessonFormData || !currentVersion) {
        return (
            <LoadingBox>
                <CircularProgress color="primary" />
            </LoadingBox>
        );
    }

    return (
        <UpdateLessonPageLayout
            lessonData={lessonData}
            lessonFormData={lessonFormData}
            tags={tagsOrEmpty}
            formValidationErrors={formValidationErrors}
            currentLanguage={currentLanguage}
            pendingNewVersion={pendingNewVersion}
            onFormDataChange={handleFormDataChange}
            onLanguageChange={handleLanguageChange}
            onRefresh={handleRefresh}
            onAddNewVersion={handleAddNewVersion}
            onSave={handleSaveClick}
            isUpdating={isUpdating}
            isDialogOpen={isDialogOpen}
            onCancelDialog={handleCancelDialog}
            onConfirmDialog={handleConfirmDialog}
            fetchTrainingWithVersionsAndProgressPending={fetchTrainingWithVersionsAndProgressPending}
            onExportToVrClick={handleExportToVrClick}
            onExportClick={handleExportClick}
            onImportClick={handleImportClick}
            showImportButton
            showExportFileButton
        >
            <NodeEditor
                key={`${lessonData.lessonId}-${currentLanguage}`}
                lessonId={lessonData.lessonId}
                config={"nodesConfiguration" in currentVersion ? currentVersion.nodesConfiguration : undefined}
                onEditorChange={onEditorChange}
            />
            <StyledHiddenInput
                ref={inputRef}
                accept="text/plain"
                type="file"
                onChange={handleImportFileOnChange}
            />
        </UpdateLessonPageLayout>
    );
};
